import React, { useState, useMemo } from 'react';
import {
  HelpCircle,
  Search,
  ChevronDown,
  User,
  Wrench,
  ShieldCheck,
  MessageSquare,
  ArrowRight,
  ExternalLink,
} from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { TELEGRAM_CHAT_URL, TELEGRAM_BOT_USERNAME } from '../../config/telegram';
import { TelegramIcon } from '../../components/TelegramAssistantSection';

interface FAQItem {
  id: string;
  category: 'customers' | 'professionals' | 'whatsapp' | 'verification';
  question: string;
  answer: string;
}

const FAQS: FAQItem[] = [
  // Customers
  {
    id: 'c1',
    category: 'customers',
    question: 'What is JobMatch NG?',
    answer:
      'JobMatch NG is a Nigerian service marketplace that connects homeowners, tenants, and commercial facility managers with verified skilled professionals and technicians (plumbers, electricians, carpenters, AC specialists, generator mechanics, and more).',
  },
  {
    id: 'c2',
    category: 'customers',
    question: 'How do I post a job on JobMatch NG?',
    answer:
      'Click "Post a Job", describe your issue or project, specify your Nigerian State and Local Government Area (LGA), and optionally attach photos or short videos. Posting a job is 100% free.',
  },
  {
    id: 'c3',
    category: 'customers',
    question: 'Can I upload photos or video of the repair problem?',
    answer:
      'Yes! Uploading photos or videos helps professionals see the exact pipe, switchboard, machine model, or damage so they can provide realistic, itemized quotes without guessing.',
  },
  {
    id: 'c4',
    category: 'customers',
    question: 'How are professionals matched to my job request?',
    answer:
      'JobMatch NG analyzes your job trade, complexity, and location to notify active, verified professionals registered in your specific Local Government Area (LGA) or nearby service zones.',
  },
  {
    id: 'c5',
    category: 'customers',
    question: 'How do I compare quotes from professionals?',
    answer:
      'When professionals respond, you will see itemized proposals detailing labor fees and material estimates in Naira, along with the professional’s verification badge, years of experience, and ratings.',
  },
  {
    id: 'c6',
    category: 'customers',
    question: 'Can I communicate with the professional before work starts?',
    answer:
      'Yes. Once you select a quote, direct phone contact and in-app chat are immediately unlocked so you can coordinate site visits, timing, and material purchases.',
  },
  {
    id: 'c7',
    category: 'customers',
    question: 'How does payment work for the physical job?',
    answer:
      'Clients pay professionals directly based on their agreed quote. We strongly recommend confirming all quote items in writing through the platform and inspecting the work before final settlement.',
  },
  {
    id: 'c8',
    category: 'customers',
    question: 'Can I cancel a job request?',
    answer:
      'Yes, you can cancel an open job request at any time before selecting a professional directly from your Customer Dashboard.',
  },
  {
    id: 'c9',
    category: 'customers',
    question: 'How do I leave a review for a professional?',
    answer:
      'After the professional marks the work as finished, you can rate their punctuality, workmanship, and communication, and leave written feedback visible on their public profile.',
  },

  // Professionals
  {
    id: 'p1',
    category: 'professionals',
    question: 'How do I register as a skilled professional or technician?',
    answer:
      'Click "Become a Professional", fill out your profile details, select your trade categories, specify your coverage LGAs, and complete the 4-step verification process.',
  },
  {
    id: 'p2',
    category: 'professionals',
    question: 'What verification documents are required to become verified?',
    answer:
      'You will need a valid government-issued ID (NIN Slip/Card, International Passport, Driver’s License, or Voter’s Card), a recent utility bill confirming your operational address, and completion of a live facial liveness check.',
  },
  {
    id: 'p3',
    category: 'professionals',
    question: 'Why is live facial verification required?',
    answer:
      'Real-time biometric facial matching ensures that the person operating the account matches the government identification submitted, preventing identity fraud and protecting the platform’s reputation.',
  },
  {
    id: 'p4',
    category: 'professionals',
    question: 'How much does it cost for a professional to use JobMatch NG?',
    answer:
      'Registration, trade profile creation, and quoting on jobs are 100% free with ₦0 monthly subscription fees. JobMatch NG charges only a standard 10% platform commission on completed jobs paid by customers via Paystack escrow.',
  },
  {
    id: 'p5',
    category: 'professionals',
    question: 'How do I receive payments for completed jobs?',
    answer:
      'When a customer accepts your quote, they deposit the agreed amount into Paystack escrow. Once the job is completed, your 90% net earnings are immediately credited and disbursed directly to your Nigerian commercial or microfinance bank account.',
  },
  {
    id: 'p6',
    category: 'professionals',
    question: 'How do I submit an itemized quote to a customer?',
    answer:
      'When an eligible job alert arrives in your dashboard, click "Submit Quote", specify your labor cost, expected turnaround days, and an estimate for required materials.',
  },

  // WhatsApp
  {
    id: 'w1',
    category: 'whatsapp',
    question: 'Can I contact JobMatch NG on WhatsApp?',
    answer:
      'Yes! You can contact the official JobMatch NG support desk on WhatsApp at +234 905 666 6582 for onboarding questions, verification help, or customer assistance.',
  },
  {
    id: 'w2',
    category: 'whatsapp',
    question: 'Is WhatsApp job booking available?',
    answer:
      'JobMatch NG is actively developing and expanding WhatsApp-based job assistance. You can always message our team on WhatsApp for immediate guidance or post directly on the website.',
  },

  // Verification & Safety
  {
    id: 'v1',
    category: 'verification',
    question: 'Does verification guarantee that nothing will go wrong?',
    answer:
      'Verification helps JobMatch NG confirm information provided by professionals (identity, address, face match). It does not guarantee perfection, honesty, or job outcome. Customers must always inspect work and exercise reasonable care.',
  },
  {
    id: 'v2',
    category: 'verification',
    question: 'How do I report a problem or dispute?',
    answer:
      'You can report issues directly to support@jobmatch.ng or via our official WhatsApp assistance line (+234 905 666 6582). Our Trust Desk reviews and investigates grievances objectively.',
  },
];

export const FaqPage: React.FC = () => {
  const { navigateTo } = useApp();
  const [activeCategory, setActiveCategory] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [openIds, setOpenIds] = useState<Record<string, boolean>>({ c1: true, p4: true, w1: true });

  const toggleFaq = (id: string) => {
    setOpenIds((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const filteredFaqs = useMemo(() => {
    return FAQS.filter((faq) => {
      if (activeCategory !== 'all' && faq.category !== activeCategory) {
        return false;
      }
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        return faq.question.toLowerCase().includes(q) || faq.answer.toLowerCase().includes(q);
      }
      return true;
    });
  }, [activeCategory, searchQuery]);

  return (
    <div className="min-h-screen bg-slate-50 py-8 px-4 sm:px-6 lg:px-8 space-y-12">
      {/* Hero Header */}
      <div className="max-w-4xl mx-auto text-center space-y-4 pt-4 sm:pt-8">
        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-100 text-emerald-800 text-xs font-bold border border-emerald-200">
          <HelpCircle className="w-4 h-4 text-emerald-600" />
          <span>Knowledge & Help Center</span>
        </span>
        <h1 className="text-3xl sm:text-5xl font-extrabold text-slate-900 tracking-tight">
          Frequently Asked Questions
        </h1>
        <p className="text-sm sm:text-base text-slate-600 max-w-xl mx-auto leading-relaxed">
          Everything you need to know about posting jobs, professional verification, quoting, and marketplace policies.
        </p>

        {/* Search Input */}
        <div className="max-w-xl mx-auto relative pt-2">
          <Search className="w-5 h-5 text-slate-400 absolute left-4 top-5" />
          <input
            type="text"
            placeholder="Search questions (e.g. quote, verification, commission, WhatsApp)..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-12 pr-4 py-3 rounded-2xl border border-slate-200 bg-white text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 shadow-xs"
          />
        </div>

        {/* Filter Pills */}
        <div className="flex flex-wrap items-center justify-center gap-2 pt-2">
          {[
            { id: 'all', label: 'All Questions' },
            { id: 'customers', label: 'For Customers' },
            { id: 'professionals', label: 'For Professionals' },
            { id: 'verification', label: 'Verification & Trust' },
            { id: 'whatsapp', label: 'WhatsApp Assistance' },
          ].map((cat) => (
            <button
              key={cat.id}
              onClick={() => setActiveCategory(cat.id)}
              className={`px-4 py-2 rounded-xl text-xs font-bold transition-all cursor-pointer ${
                activeCategory === cat.id
                  ? 'bg-emerald-600 text-white shadow-xs'
                  : 'bg-white border border-slate-200 text-slate-700 hover:bg-slate-100'
              }`}
            >
              {cat.label}
            </button>
          ))}
        </div>
      </div>

      {/* Accordion FAQ List */}
      <div className="max-w-3xl mx-auto space-y-3">
        {filteredFaqs.length === 0 ? (
          <div className="bg-white rounded-3xl p-8 border border-slate-200 text-center space-y-3">
            <HelpCircle className="w-10 h-10 text-slate-400 mx-auto" />
            <h4 className="font-bold text-slate-900 text-sm">No matching questions found</h4>
            <p className="text-xs text-slate-500">
              Try searching with a different term or reach our support team on WhatsApp.
            </p>
          </div>
        ) : (
          filteredFaqs.map((faq) => {
            const isOpen = !!openIds[faq.id];
            return (
              <div
                key={faq.id}
                className="bg-white rounded-2xl border border-slate-200 shadow-xs overflow-hidden transition-all"
              >
                <button
                  onClick={() => toggleFaq(faq.id)}
                  className="w-full p-4 sm:p-5 text-left flex items-center justify-between gap-4 cursor-pointer hover:bg-slate-50/50"
                  aria-expanded={isOpen}
                >
                  <span className="font-bold text-xs sm:text-sm text-slate-900 leading-snug">
                    {faq.question}
                  </span>
                  <ChevronDown
                    className={`w-4 h-4 text-slate-400 shrink-0 transition-transform duration-200 ${
                      isOpen ? 'rotate-180 text-emerald-600' : ''
                    }`}
                  />
                </button>

                {isOpen && (
                  <div className="px-4 pb-5 sm:px-5 sm:pb-5 pt-0 text-xs sm:text-sm text-slate-600 leading-relaxed border-t border-slate-100 mt-1">
                    {faq.answer}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Telegram Assistance Banner */}
      <div className="max-w-3xl mx-auto bg-gradient-to-r from-slate-900 to-sky-950 text-white border border-sky-800/40 rounded-3xl p-6 sm:p-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 shadow-xl">
        <div className="space-y-1">
          <span className="text-xs font-bold text-sky-400 uppercase tracking-wider block">
            Still Have Questions?
          </span>
          <h3 className="font-extrabold text-base text-white">
            Chat with Our Official Telegram AI Assistant
          </h3>
          <p className="text-xs text-slate-300 leading-relaxed">
            Need quick answers or instant professional matching? Connect with our AI on Telegram at{' '}
            <strong className="text-sky-300 font-mono">@{TELEGRAM_BOT_USERNAME}</strong>.
          </p>
        </div>

        <a
          href={TELEGRAM_CHAT_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="px-5 py-3 rounded-xl bg-sky-500 hover:bg-sky-400 text-slate-950 font-black text-xs shadow-md transition-all inline-flex items-center gap-2 shrink-0 cursor-pointer"
        >
          <TelegramIcon className="w-4 h-4 fill-slate-950" />
          <span>Start Telegram Chat</span>
          <ExternalLink className="w-3.5 h-3.5 opacity-70" />
        </a>
      </div>
    </div>
  );
};
